<script lang="ts">
export default { name: 'Otp' };
</script>

<script setup lang="ts">
// COMPONENTS
import { GButton } from '@flash-global66/g-button';
import { GIconFont } from '@flash-global66/g-icon-font';
import { GInputCode } from '@flash-global66/g-input-code';

// COMPOSABLES
import { useOtp } from './useOtp';
import { useRouter } from 'vue-router';

// HELPERS
import { getAsset } from '@/helpers/assets';

// TYPES
import type { OtpEmits } from '../../types/otp.type';
import { OtpChannelEnum } from '../../types/otp.type';

const emits = defineEmits<OtpEmits>();

const router = useRouter();

const {
  otpFlow,
  accountStore,
  zIndex,
  shouldShowFirstButtons,
  shippingTypeInfo,
  isFinished,
  isLoadingOtp,
  isChannelDisabled,
  isChannelLoading,
  shippingTypeMap,
  FIELDS,
  countdownMinutes,
  countdownSeconds,
  inputCodeRef,
  isRecaptchaSectionVisible,
  isRecaptchaPending,
  isOtpInputBlocked,
  recaptchaV2RequiredMessage,
  recaptchaSectionRef,
  recaptchaV2ContainerId,
  onChange,
  onComplete,
  requestOtp,
  onClose,
} = useOtp(emits, router);
</script>

<template>
  <div
    v-if="otpFlow.isVisible"
    data-test="modal:otp"
    :style="{ zIndex }"
    class="w-full h-full fixed flex flex-col top-0 left-0 bg-gray-10 py-6 items-center px-5"
  >
    <div class="flex flex-col flex-1 justify-center items-center max-w-lg">
      <img
        class="h-28 mb-8"
        :src="getAsset('img/security-phone.png')"
        alt="security"
      />
      <section class="text-center">
        <h4 class="text-8 text-blue-14 font-semibold">
          {{
            !shouldShowFirstButtons
              ? 'Introduce el código de 6 dígitos'
              : 'Escoge el método de envío de tu código de verificación'
          }}
        </h4>
        <h5
          class="text-blue-12 text-5 mt-4 font-medium"
          v-if="!shouldShowFirstButtons"
        >
          {{ isLoadingOtp ? 'Enviado' : 'Lo enviamos' }} por
          <g-icon-font :name="shippingTypeInfo.icon" />
          {{ shippingTypeInfo.text }}
          <template v-if="otpFlow.requestType.value === 'WITH_SESSION'">
            <br />
            al
            <strong v-if="shippingTypeInfo.text === 'Email'">
              {{ accountStore?.kycStatus?.b2bUser?.email }}
            </strong>
            <strong v-else>
              {{ accountStore?.kycStatus?.b2bUser?.phoneNumber }}
            </strong>
          </template>
        </h5>
      </section>
      <section class="my-8 px-2" v-if="!shouldShowFirstButtons">
        <g-input-code
          :fields="FIELDS"
          :text-error="otpFlow.error.value"
          :disabled="isOtpInputBlocked"
          classContent="flex-wrap"
          @change="onChange"
          @complete="onComplete"
          data-test="input:code"
          ref="inputCodeRef"
        />
      </section>
      <p
        v-if="!isFinished && !shouldShowFirstButtons && !isRecaptchaPending"
        class="text-center text-blue-13 text-3"
      >
        ¿No te llega el código? Pídelo de nuevo en {{ countdownMinutes }}:{{
          countdownSeconds
        }}
      </p>
      <section
        v-if="isFinished || shouldShowFirstButtons"
        class="flex flex-wrap justify-center mt-4 gap-4 w-[120%] max-w-xl"
      >
        <g-button
          v-if="!otpFlow.hideInputs.value.includes(OtpChannelEnum.SMS)"
          data-test="button:send-sms"
          variant="secondary"
          :disabled="isChannelDisabled(OtpChannelEnum.SMS)"
          @click="requestOtp(OtpChannelEnum.SMS)"
          :icon-left="shippingTypeMap[OtpChannelEnum.SMS].icon"
          :loading="isChannelLoading(OtpChannelEnum.SMS)"
        >
          Enviar por SMS
        </g-button>
        <g-button
          v-if="!otpFlow.hideInputs.value.includes(OtpChannelEnum.WHATSAPP)"
          data-test="button:send-whatsapp"
          variant="secondary"
          :disabled="isChannelDisabled(OtpChannelEnum.WHATSAPP)"
          @click="requestOtp(OtpChannelEnum.WHATSAPP)"
          :icon-left="shippingTypeMap[OtpChannelEnum.WHATSAPP].icon"
          :loading="isChannelLoading(OtpChannelEnum.WHATSAPP)"
        >
          Enviar por Whatsapp
        </g-button>
        <g-button
          v-if="!otpFlow.hideInputs.value.includes(OtpChannelEnum.EMAIL)"
          data-test="button:send-email"
          variant="secondary"
          :disabled="isChannelDisabled(OtpChannelEnum.EMAIL)"
          @click="requestOtp(OtpChannelEnum.EMAIL)"
          :icon-left="shippingTypeMap[OtpChannelEnum.EMAIL].icon"
          :loading="isChannelLoading(OtpChannelEnum.EMAIL)"
        >
          Enviar por Correo
        </g-button>
      </section>
    </div>
    <div
      v-show="isRecaptchaSectionVisible"
      ref="recaptchaSectionRef"
      class="bg-sec-def-bg border border-secondary-bd rounded-md p-4 my-2 transition-all duration-300 ease-out mx-auto mt-6 mb-4"
    >
      <div class="mb-3">
        <p class="text-3 text-primary-txt text-center">
          {{ recaptchaV2RequiredMessage }}
        </p>
      </div>
      <div class="flex justify-center min-h-20">
        <div :id="recaptchaV2ContainerId" />
      </div>
    </div>
    <section class="mt-auto mx-auto max-w-xl flex justify-center w-full gap-2">
      <g-button
        full
        class="mr-3"
        variant="secondary"
        data-test="button:Cancel"
        @click="onClose"
      >
        Volver
      </g-button>
    </section>
  </div>
</template>
